// components/ScheduleMeetingModal.js
import React, { useState, useEffect } from 'react';
import '../styles/ScheduleMeetingModal.css';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';

const ScheduleMeetingModal = ({ contact, isOpen, onClose, onSuccess }) => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewEmail, setPreviewEmail] = useState(null);
  const [userSettings, setUserSettings] = useState(null);

  // Prevent background scrolling while modal is open (mobile-friendly).
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [isOpen]);

  // Form state
  const [formData, setFormData] = useState({
    topic: '',
    durationMinutes: 30,
    preferredTimingPresets: [],
    manualSlots: [],
    meetingType: 'video_call',
    schedulingMethod: 'propose_times',
    preview: true
  });

  // Preview email state
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody, setPreviewBody] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        topic: '',
        durationMinutes: 30,
        preferredTimingPresets: [],
        manualSlots: [],
        meetingType: 'video_call',
        schedulingMethod: 'propose_times',
        preview: true
      });
      setPreviewMode(false);
      setPreviewEmail(null);
      fetchUserSettings();
    }
  }, [isOpen, contact]);

  const fetchUserSettings = async () => {
    try {
      const response = await api.get('/api/user/settings');
      setUserSettings(response.data);
      // If user has scheduling link, set it as default method
      if (response.data.schedulingLink) {
        setFormData(prev => ({ ...prev, schedulingMethod: 'share_booking_link' }));
      }
    } catch (error) {
      console.error('Error fetching user settings:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePresetToggle = (preset) => {
    console.log('Toggle preset clicked:', preset);
    setFormData(prev => {
      const currentPresets = prev.preferredTimingPresets || [];
      const presets = [...currentPresets];
      const index = presets.indexOf(preset);
      
      if (index > -1) {
        // Remove if already selected
        presets.splice(index, 1);
        console.log('Removed preset, new array:', presets);
      } else {
        // Add if not selected
        presets.push(preset);
        console.log('Added preset, new array:', presets);
      }
      
      return { ...prev, preferredTimingPresets: presets };
    });
  };

  const handleAddManualSlot = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    
    const endTime = new Date(tomorrow);
    endTime.setHours(11, 0, 0, 0);

    setFormData(prev => ({
      ...prev,
      manualSlots: [...prev.manualSlots, { start: tomorrow.toISOString(), end: endTime.toISOString() }]
    }));
  };

  const handleRemoveManualSlot = (index) => {
    setFormData(prev => ({
      ...prev,
      manualSlots: prev.manualSlots.filter((_, i) => i !== index)
    }));
  };

  const handleManualSlotChange = (index, field, value) => {
    setFormData(prev => {
      const slots = [...prev.manualSlots];
      slots[index] = { ...slots[index], [field]: value };
      return { ...prev, manualSlots: slots };
    });
  };

  const validateForm = () => {
    if (!formData.topic.trim()) {
      showError('Please enter a topic for the meeting');
      return false;
    }
    if (formData.topic.length > 500) {
      showError('Topic must be 500 characters or less');
      return false;
    }
    if (formData.schedulingMethod === 'share_booking_link' && !userSettings?.schedulingLink) {
      showError('Please add a scheduling link in Account Settings');
      return false;
    }
    if (formData.preferredTimingPresets.length === 0 && formData.manualSlots.length === 0 && formData.schedulingMethod === 'propose_times') {
      showError('Please select at least one time preference or add manual time slots');
      return false;
    }
    return true;
  };

  const handleGenerateAndSend = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        contactId: contact?._id || null,
        contactEmail: contact?.email || '',
        topic: formData.topic.trim(),
        durationMinutes: formData.durationMinutes,
        preferredTimingPresets: formData.preferredTimingPresets,
        manualSlots: formData.manualSlots,
        meetingType: formData.meetingType,
        schedulingMethod: formData.schedulingMethod,
        preview: formData.preview
      };

      const response = await api.post('/api/meetings/schedule', payload);

      if (formData.preview) {
        // Show preview
        setPreviewEmail(response.data);
        setPreviewSubject(response.data.email.subject);
        setPreviewBody(response.data.email.body);
        setPreviewMode(true);
      } else {
        // Direct send
        showSuccess('Meeting request sent via Velox');
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      showError(error.response?.data?.error || 'Failed to schedule meeting');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!previewEmail?.meetingRequestId) {
      showError('Invalid meeting request');
      return;
    }

    if (!previewSubject.trim() || !previewBody.trim()) {
      showError('Please fill in both subject and body');
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/meetings/send', {
        meetingRequestId: previewEmail.meetingRequestId,
        subject: previewSubject.trim(),
        body: previewBody.trim()
      });

      showSuccess('Meeting request sent via Velox');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      showError(error.response?.data?.error || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="schedule-meeting-modal-overlay" onClick={onClose}>
      <div className="schedule-meeting-modal" onClick={(e) => e.stopPropagation()}>
        {!previewMode ? (
          <>
            <div className="schedule-meeting-modal-header">
              <h3>Schedule Meeting</h3>
              <button className="schedule-meeting-modal-close" onClick={onClose}>×</button>
            </div>

            <div className="schedule-meeting-modal-content">
              {/* Contact Info (Read-only) */}
              <div className="schedule-meeting-field">
                <label>Contact</label>
                <div className="schedule-meeting-contact-display">
                  <div className="contact-name">{contact?.name || 'Unknown'}</div>
                  <div className="contact-email">{contact?.email || ''}</div>
                  {contact?.company && <div className="contact-company">{contact.company}</div>}
                </div>
              </div>

              {/* Topic */}
              <div className="schedule-meeting-field">
                <label htmlFor="topic">Topic / Purpose of Meeting <span className="required">*</span></label>
                <textarea
                  id="topic"
                  value={formData.topic}
                  onChange={(e) => handleInputChange('topic', e.target.value)}
                  placeholder="e.g., Discuss potential collaboration opportunities"
                  rows={3}
                  maxLength={500}
                  className="schedule-meeting-textarea"
                />
                <div className="schedule-meeting-char-count">{formData.topic.length}/500</div>
              </div>

              {/* Duration */}
              <div className="schedule-meeting-field">
                <label>Duration <span className="required">*</span></label>
                <div className="schedule-meeting-radio-group">
                  {[15, 30, 45, 60].map(duration => (
                    <label key={duration} className="schedule-meeting-radio">
                      <input
                        type="radio"
                        name="duration"
                        value={duration}
                        checked={formData.durationMinutes === duration}
                        onChange={(e) => handleInputChange('durationMinutes', parseInt(e.target.value))}
                      />
                      <span>{duration} min</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Preferred Timing */}
              <div className="schedule-meeting-field">
                <label>Preferred Timing</label>
                <div className="schedule-meeting-preset-buttons" onClick={(e) => e.stopPropagation()}>
                  {[
                    { value: 'this_week', label: 'This week' },
                    { value: 'next_week', label: 'Next week' },
                    { value: 'mornings_only', label: 'Mornings only' },
                    { value: 'afternoons_only', label: 'Afternoons only' },
                    { value: 'anytime', label: 'Anytime' }
                  ].map(preset => {
                    const isActive = (formData.preferredTimingPresets || []).includes(preset.value);
                    return (
                      <button
                        key={preset.value}
                        type="button"
                        className={`schedule-meeting-preset-button ${isActive ? 'active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Button clicked for:', preset.value);
                          console.log('Current presets before toggle:', formData.preferredTimingPresets);
                          console.log('Is active before toggle:', isActive);
                          handlePresetToggle(preset.value);
                        }}
                        style={{ pointerEvents: 'auto' }}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Manual Time Slots */}
              <div className="schedule-meeting-field">
                <label>Manual Time Slots (Optional)</label>
                {formData.manualSlots.map((slot, index) => {
                  const startDate = new Date(slot.start);
                  const endDate = new Date(slot.end);
                  return (
                    <div key={index} className="schedule-meeting-manual-slot">
                      <input
                        type="datetime-local"
                        value={startDate.toISOString().slice(0, 16)}
                        onChange={(e) => handleManualSlotChange(index, 'start', new Date(e.target.value).toISOString())}
                        className="schedule-meeting-datetime-input"
                      />
                      <span>to</span>
                      <input
                        type="datetime-local"
                        value={endDate.toISOString().slice(0, 16)}
                        onChange={(e) => handleManualSlotChange(index, 'end', new Date(e.target.value).toISOString())}
                        className="schedule-meeting-datetime-input"
                      />
                      <button
                        type="button"
                        className="schedule-meeting-remove-slot"
                        onClick={() => handleRemoveManualSlot(index)}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="schedule-meeting-add-slot"
                  onClick={handleAddManualSlot}
                >
                  + Add Time Slot
                </button>
              </div>

              {/* Meeting Type */}
              <div className="schedule-meeting-field">
                <label htmlFor="meetingType">Meeting Type <span className="required">*</span></label>
                <select
                  id="meetingType"
                  value={formData.meetingType}
                  onChange={(e) => handleInputChange('meetingType', e.target.value)}
                  className="schedule-meeting-select"
                >
                  <option value="video_call">Video call</option>
                  <option value="phone_call">Phone call</option>
                  <option value="in_person">In person</option>
                </select>
              </div>

              {/* Scheduling Method */}
              <div className="schedule-meeting-field">
                <label>Scheduling Method <span className="required">*</span></label>
                <div className="schedule-meeting-radio-group">
                  <label className="schedule-meeting-radio">
                    <input
                      type="radio"
                      name="schedulingMethod"
                      value="propose_times"
                      checked={formData.schedulingMethod === 'propose_times'}
                      onChange={(e) => handleInputChange('schedulingMethod', e.target.value)}
                    />
                    <span>Ask if these times work</span>
                  </label>
                  <label className="schedule-meeting-radio">
                    <input
                      type="radio"
                      name="schedulingMethod"
                      value="share_booking_link"
                      checked={formData.schedulingMethod === 'share_booking_link'}
                      onChange={(e) => handleInputChange('schedulingMethod', e.target.value)}
                      disabled={!userSettings?.schedulingLink}
                    />
                    <span>
                      Share my booking link
                      {!userSettings?.schedulingLink && <span className="schedule-meeting-disabled-hint"> (Add in Account Settings)</span>}
                    </span>
                  </label>
                  <label className="schedule-meeting-radio schedule-meeting-radio-disabled">
                    <input
                      type="radio"
                      name="schedulingMethod"
                      value="direct_calendar_invite"
                      disabled={true}
                    />
                    <span>Send direct calendar invite <span className="schedule-meeting-coming-soon">(Coming soon)</span></span>
                  </label>
                </div>
              </div>

              {/* Preview Toggle */}
              <div className="schedule-meeting-field">
                <label className="schedule-meeting-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.preview}
                    onChange={(e) => handleInputChange('preview', e.target.checked)}
                  />
                  <span>Preview email before sending</span>
                </label>
              </div>
            </div>

            <div className="schedule-meeting-modal-buttons">
              <button
                className="schedule-meeting-button-cancel"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="schedule-meeting-button-primary"
                onClick={handleGenerateAndSend}
                disabled={loading}
              >
                {loading ? 'Generating...' : formData.preview ? 'Generate & Preview' : 'Generate & Send'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="schedule-meeting-modal-header">
              <h3>Preview Email</h3>
              <button className="schedule-meeting-modal-close" onClick={() => setPreviewMode(false)}>×</button>
            </div>

            <div className="schedule-meeting-modal-content">
              <div className="schedule-meeting-field">
                <label htmlFor="previewSubject">Subject</label>
                <input
                  id="previewSubject"
                  type="text"
                  value={previewSubject}
                  onChange={(e) => setPreviewSubject(e.target.value)}
                  className="schedule-meeting-input"
                  maxLength={200}
                />
              </div>

              <div className="schedule-meeting-field">
                <label htmlFor="previewBody">Body</label>
                <textarea
                  id="previewBody"
                  value={previewBody}
                  onChange={(e) => setPreviewBody(e.target.value)}
                  rows={12}
                  className="schedule-meeting-textarea"
                />
              </div>
            </div>

            <div className="schedule-meeting-modal-buttons">
              <button
                className="schedule-meeting-button-cancel"
                onClick={() => setPreviewMode(false)}
                disabled={loading}
              >
                Back
              </button>
              <button
                className="schedule-meeting-button-primary"
                onClick={handleSendEmail}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ScheduleMeetingModal;

